import React, { useEffect, useState } from 'react';
import {
  FiFileText,
  FiUsers,
  FiClock,
  FiAward,
  FiArrowRight,
  FiArrowUpRight,
  FiCheckCircle,
  FiXCircle,
  FiInbox,
  FiCreditCard
} from 'react-icons/fi';

import { db } from '../services/api';
import {
  requestService,
  certificateService
} from '../services/supabaseClient';

const KPI_ICONS = {
  content: { icon: FiFileText, tone: 'primary' },
  partners: { icon: FiUsers, tone: 'blue' },
  pending: { icon: FiClock, tone: 'amber' },
  certificates: { icon: FiAward, tone: 'green' }
};

function formatDate(value) {
  if (!value) return '—';

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return '—';

  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function partnerLabel(row) {
  return (
    row?.partners?.company_name ||
    row?.company_name ||
    [
      row?.partners?.first_name || row?.first_name,
      row?.partners?.last_name || row?.last_name
    ]
      .filter(Boolean)
      .join(' ') ||
    '—'
  );
}

function personName(row) {
  const name = [
    row?.students?.first_name,
    row?.students?.last_name
  ]
    .filter(Boolean)
    .join(' ');

  return name || row?.person_name || '—';
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();

  return (
    <span className={`status ${s}`}>
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: 'currentColor',
          display: 'inline-block',
          marginRight: 5,
          verticalAlign: 1
        }}
      />
      {status || '—'}
    </span>
  );
}

function InitialsAvatar({ name }) {
  const initials =
    (name || '?')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || '?';

  return (
    <span
      className="avatar"
      style={{
        width: 24,
        height: 24,
        fontSize: 8.5
      }}
    >
      {initials}
    </span>
  );
}

function EmptyState({
  icon: Icon = FiInbox,
  label
}) {
  return (
    <div
      className="empty-row"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6
      }}
    >
      <Icon
        size={18}
        style={{ opacity: 0.4 }}
      />

      {label}
    </div>
  );
}

export default function AdminDashboard({ go }) {
  const [stats, setStats] = useState({
    content: 0,
    partners: 0,
    requests: 0,
    certificates: 0,
    pending: 0
  });

  const [recentCertificates, setRecentCertificates] = useState([]);
  const [recentPartners, setRecentPartners] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        const [
          content,
          partners,
          requests,
          certificates
        ] = await Promise.all([
          db.content.list(),
          db.partners.list(),
          requestService.getAll(),
          certificateService.getAll()
        ]);

        const contentRows = content.data || [];
        const partnerRows = partners.data || [];

        // Only actual certificate requests
        // "registered" rows should not be shown
        const requestRows = (requests || []).filter(
          (request) => request.status !== 'registered'
        );

        const certificateRows = certificates || [];

        setStats({
          content: contentRows.length,
          partners: partnerRows.length,
          requests: requestRows.length,
          certificates: certificateRows.length,
          pending: requestRows.filter(
            (row) => row.status === 'pending'
          ).length
        });

        // ================================
        // RECENT CERTIFICATES
        // ================================

        setRecentCertificates(
          [...certificateRows]
            .sort(
              (a, b) =>
                new Date(
                  b.created_at ||
                  b.issue_date ||
                  0
                ) -
                new Date(
                  a.created_at ||
                  a.issue_date ||
                  0
                )
            )
            .slice(0, 5)
        );

        // ================================
        // RECENT PARTNERS
        // ================================

        setRecentPartners(
          [...partnerRows]
            .sort(
              (a, b) =>
                new Date(b.created_at || 0) -
                new Date(a.created_at || 0)
            )
            .slice(0, 5)
        );

        // ================================
        // RECENT REQUESTS
        // Same data structure as Requests.jsx
        // ================================

        setRecentRequests(
          [...requestRows]
            .sort(
              (a, b) =>
                new Date(b.created_at || 0) -
                new Date(a.created_at || 0)
            )
            .slice(0, 5)
        );

      } catch (error) {
        console.error(
          'Unable to load dashboard data:',
          error
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const kpis = [
    {
      key: 'content',
      label: 'Total Content',
      value: stats.content,
      route: 'content',
      hint: 'Courses & modules'
    },
    {
      key: 'partners',
      label: 'Partners',
      value: stats.partners,
      route: 'partners',
      hint: 'Registered partners'
    },
    {
      key: 'pending',
      label: 'Pending Requests',
      value: stats.pending,
      route: 'requests',
      hint: `of ${stats.requests} total`
    },
    {
      key: 'certificates',
      label: 'Certificates',
      value: stats.certificates,
      route: 'certificates',
      hint: 'Issued till date'
    }
  ];

  return (
    <div>

      {/* HERO SECTION */}

      <div className="hero">
        <div>
          <span className="eyebrow">
            OVERVIEW
          </span>

          <h2>
            Good to see you, Admin.
          </h2>

          <p>
            Manage your content, partners and certification workflow.
          </p>
        </div>

        <button
          className="primary"
          onClick={() => go('content')}
          type="button"
        >
          <FiFileText
            size={13}
            style={{
              marginRight: 6,
              verticalAlign: -2
            }}
          />

          Create Content
        </button>
      </div>


      {/* KPI CARDS */}

      <div className="kpi-grid">

        {kpis.map(
          ({
            key,
            label,
            value,
            route,
            hint
          }) => {

            const {
              icon: Icon,
              tone
            } = KPI_ICONS[key];

            return (
              <button
                className={`kpi-card tone-${tone} kpi-card-accent`}
                key={key}
                type="button"
                onClick={() => go(route)}
              >

                <div className="kpi-card-top">

                  <div className="kpi-icon">
                    <Icon size={18} />
                  </div>

                  <span className="kpi-link">
                    View
                    <FiArrowRight size={11} />
                  </span>

                </div>

                <b className="kpi-value">
                  {loading ? '—' : value}
                </b>

                <span className="kpi-label">
                  {label}
                </span>

                <span className="kpi-hint">
                  {hint}
                </span>

              </button>
            );
          }
        )}

      </div>


      {/* RECENT ACTIVITY */}

      <div className="recent-grid">


        {/* RECENT CERTIFICATES */}

        <div className="panel">

          <div className="panel-head">

            <div>

              <h3>
                Recent Certificates
              </h3>

              <p>
                Latest issued certificates
              </p>

            </div>

            <button
              className="secondary"
              onClick={() => go('certificates')}
              type="button"
            >
              View all

              <FiArrowUpRight
                size={11}
                style={{
                  marginLeft: 4,
                  verticalAlign: -1
                }}
              />

            </button>

          </div>


          <div className="table-wrap flat">

            <table>

              <thead>

                <tr>
                  <th>Person</th>
                  <th>Course</th>
                  <th>Issued</th>
                  <th>Status</th>
                </tr>

              </thead>


              <tbody>

                {recentCertificates.length === 0 &&
                  !loading && (

                    <tr>

                      <td colSpan={4}>

                        <EmptyState
                          icon={FiAward}
                          label="No certificates issued yet"
                        />

                      </td>

                    </tr>

                  )}


                {recentCertificates.map(
                  (certificate) => {

                    const certificatePerson =
                      certificate.students
                        ? [
                            certificate.students.first_name,
                            certificate.students.last_name
                          ]
                            .filter(Boolean)
                            .join(' ')
                        : certificate.person_name || '—';

                    const certificateCourse =
                      certificate.course?.name ||
                      certificate.contents?.name ||
                      certificate.content?.name ||
                      '—';

                    return (

                      <tr key={certificate.id}>

                        <td>

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 7
                            }}
                          >

                            <InitialsAvatar
                              name={certificatePerson}
                            />

                            <b>
                              {certificatePerson}
                            </b>

                          </div>

                        </td>


                        <td>
                          {certificateCourse}
                        </td>


                        <td>
                          {formatDate(
                            certificate.created_at ||
                            certificate.issue_date
                          )}
                        </td>


                        <td>

                          <StatusPill
                            status={
                              certificate.status ||
                              'issued'
                            }
                          />

                        </td>

                      </tr>

                    );
                  }
                )}

              </tbody>

            </table>

          </div>

        </div>


        {/* RECENT PARTNERS */}

        <div className="panel">

          <div className="panel-head">

            <div>

              <h3>
                Recent Partners
              </h3>

              <p>
                Newly registered partners
              </p>

            </div>


            <button
              className="secondary"
              onClick={() => go('partners')}
              type="button"
            >

              View all

              <FiArrowUpRight
                size={11}
                style={{
                  marginLeft: 4,
                  verticalAlign: -1
                }}
              />

            </button>

          </div>


          <div className="table-wrap flat">

            <table>

              <thead>

                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Joined</th>
                  <th>Status</th>
                </tr>

              </thead>


              <tbody>

                {recentPartners.length === 0 &&
                  !loading && (

                    <tr>

                      <td colSpan={4}>

                        <EmptyState
                          icon={FiUsers}
                          label="No partners registered yet"
                        />

                      </td>

                    </tr>

                  )}


                {recentPartners.map(
                  (partner) => (

                    <tr key={partner.id}>

                      <td>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 7
                          }}
                        >

                          <InitialsAvatar
                            name={partnerLabel(partner)}
                          />

                          <b>
                            {partnerLabel(partner)}
                          </b>

                        </div>

                      </td>


                      <td>
                        {partner.email || '—'}
                      </td>


                      <td>
                        {formatDate(partner.created_at)}
                      </td>


                      <td>

                        <StatusPill
                          status={
                            partner.status ||
                            'active'
                          }
                        />

                      </td>

                    </tr>

                  )
                )}

              </tbody>

            </table>

          </div>

        </div>

      </div>


      {/* RECENT REQUESTS */}

      <div className="panel">

        <div className="panel-head">

          <div>

            <h3>
              Recent Requests
            </h3>

            <p>
              Latest certification requests raised by partners
            </p>

          </div>


          <button
            className="secondary"
            onClick={() => go('requests')}
            type="button"
          >

            View all

            <FiArrowUpRight
              size={11}
              style={{
                marginLeft: 4,
                verticalAlign: -1
              }}
            />

          </button>

        </div>


        <div className="table-wrap flat">

          <table>

            <thead>

              <tr>
                <th>Person</th>
                <th>Content</th>
                <th>Partner</th>
                <th>Payment</th>
                <th>Requested On</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>
                  Action
                </th>
              </tr>

            </thead>


            <tbody>

              {recentRequests.length === 0 &&
                !loading && (

                  <tr>

                    <td colSpan={7}>

                      <EmptyState
                        icon={FiClock}
                        label="No requests yet"
                      />

                    </td>

                  </tr>

                )}


              {recentRequests.map(
                (request) => {

                  const name =
                    personName(request);

                  const isCertification =
                    request.course?.type ===
                    'certification';

                  return (

                    <tr
                      key={
                        request.enrollment_id ||
                        request.id
                      }
                    >


                      {/* PERSON */}

                      <td>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 7
                          }}
                        >

                          <InitialsAvatar
                            name={name}
                          />

                          <div>

                            <b>
                              {name}
                            </b>

                            {request.students?.email && (

                              <div
                                style={{
                                  fontSize: 11,
                                  opacity: 0.65
                                }}
                              >

                                {request.students.email}

                              </div>

                            )}

                          </div>

                        </div>

                      </td>


                      {/* CONTENT */}

                      <td>

                        {request.course?.name || '—'}

                        {request.course?.course_code
                          ? ` (${request.course.course_code})`
                          : ''}

                      </td>


                      {/* PARTNER */}

                      <td>
                        {partnerLabel(request)}
                      </td>


                      {/* PAYMENT */}

                      <td>

                        {isCertification ? (

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >

                            <FiCreditCard size={13} />

                            <span>
                              {request.payment_status || '—'}

                              {request.course?.price
                                ? ` · ₹${request.course.price}`
                                : ''}

                            </span>

                          </div>

                        ) : (

                          'Not required'

                        )}

                      </td>


                      {/* CREATED */}

                      <td>

                        {formatDate(
                          request.created_at
                        )}

                      </td>


                      {/* STATUS */}

                      <td>

                        <StatusPill
                          status={
                            request.status
                          }
                        />

                      </td>


                      {/* ACTION */}

                      <td
                        className="actions"
                        style={{
                          justifyContent: 'flex-end'
                        }}
                      >

                        {request.status ===
                        'pending' ? (

                          <>

                            <button
                              type="button"
                              title="Approve"
                              onClick={() =>
                                go('requests')
                              }
                            >

                              <FiCheckCircle
                                size={12}
                              />

                            </button>


                            <button
                              type="button"
                              title="Reject"
                              className="danger"
                              onClick={() =>
                                go('requests')
                              }
                            >

                              <FiXCircle
                                size={12}
                              />

                            </button>

                          </>

                        ) : (

                          <button
                            type="button"
                            onClick={() =>
                              go('requests')
                            }
                          >
                            View
                          </button>

                        )}

                      </td>

                    </tr>

                  );
                }
              )}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}